import { AppSiteSettings } from '@repo/models/AppSanityTypes';
import { GetSanitySiteSettingsQuery } from '@repo/models/gatsby-graphql-types';
import { useStaticQuery, graphql } from 'gatsby';

export const internalLinkTargetFields = graphql`
  fragment internalLinkTargetBlogPostFields on SanityBlogPost {
    _type
    slug {
      current
    }
  }

  fragment internalLinkTargetPageFields on SanityPage {
    _type
    slug {
      current
    }
  }

  fragment internalLinkTargetProductFields on SanityProduct {
    _type
    store {
      productType
      slug {
        current
      }
    }
  }

  fragment internalLinkTargetCollectionFields on SanityCollection {
    _type
    store {
      slug {
        current
      }
    }
  }

  fragment internalLinkTargetPersonFields on SanityPerson {
    _type
    slug {
      current
    }
  }

  fragment internalLinkTargetPressReleaseFields on SanityPressRelease {
    _type
    slug {
      current
    }
  }

  fragment internalLinkTargetSharedPageFields on SanitySharedPage {
    id
    _type
    slug {
      current
    }
  }
`;

export const sanityLinkFragment = graphql`
  fragment sanityLinkFields on SanityLink {
    href
    blank
    _type
    linkType
    caption
    internalLink {
      ...internalLinkTargetBlogPostFields
      ...internalLinkTargetPageFields
      ...internalLinkTargetProductFields
      ...internalLinkTargetCollectionFields
      ...internalLinkTargetPersonFields
      ...internalLinkTargetPressReleaseFields
      ...internalLinkTargetSharedPageFields
    }
  }
  fragment sanityLinkNoCaptionFields on SanityLinkNoCaption {
    href
    blank
    _type
    linkType
    internalLink {
      ...internalLinkTargetBlogPostFields
      ...internalLinkTargetPageFields
      ...internalLinkTargetProductFields
      ...internalLinkTargetCollectionFields
      ...internalLinkTargetPersonFields
      ...internalLinkTargetPressReleaseFields
      ...internalLinkTargetSharedPageFields
    }
  }
`;

export const sanityBlockBuilderTextFields = graphql`
  fragment sanityBlockBuilderTextFields on SanityBlockBuilderText {
    text {
      _key
      _type
      level
      listItem
      style
      children {
        _key
        _type
        marks
        text
      }
      markDefs
    }
  }
`;

const useSiteSettings = () => {
  const { sanitySiteSettings } = useStaticQuery<GetSanitySiteSettingsQuery>(
    graphql`
      query GetSanitySiteSettings {
        sanitySiteSettings(
          _type: { eq: "siteSettings" }
          siteIdentifier: { eq: "ifb" }
        ) {
          mainMenu {
            columns
            items {
              ... on SanityMenuItemMegaMenuItem {
                title
                link {
                  ...sanityLinkNoCaptionFields
                }
                menuItemSupportChat
                items {
                  title
                  link {
                    ...sanityLinkNoCaptionFields
                  }
                }
              }
              ... on SanityCollection {
                title
                store {
                  title
                  slug {
                    current
                  }
                }
              }
            }
            link {
              ...sanityLinkNoCaptionFields
            }
            menuType
            title
            isGallery
          }
          footerLegal {
            footerLinkName
            href
            linkType
            blank
            internalLink {
              ...internalLinkTargetBlogPostFields
              ...internalLinkTargetPageFields
              ...internalLinkTargetProductFields
              ...internalLinkTargetCollectionFields
              ...internalLinkTargetPersonFields
              ...internalLinkTargetPressReleaseFields
              ...internalLinkTargetSharedPageFields
            }
          }
          footerCustomColumns {
            footerColumnHeader
            footerColumnItems {
              footerLinkName
              href
              blank
              linkType
              internalLink {
                ...internalLinkTargetBlogPostFields
                ...internalLinkTargetPageFields
                ...internalLinkTargetProductFields
                ...internalLinkTargetCollectionFields
                ...internalLinkTargetPersonFields
                ...internalLinkTargetPressReleaseFields
                ...internalLinkTargetSharedPageFields
              }
              anchor
            }
          }
          footerCopyright {
            copyrightName
            copyrightUrl
          }
          notificationBanner {
            text
            link {
              ...sanityLinkFields
            }
            blockSvg {
              linkType
              svgUploaded {
                alt
                asset {
                  url
                }
              }
            }
          }
          phone
          contactPage {
            slug {
              current
            }
          }
          shipping
          manageSubscriptionMessage {
            ...sanityBlockBuilderTextFields
          }
          manageSubscriptionSupportMessage {
            ...sanityBlockBuilderTextFields
          }
          manageSubscriptionCancelMessage {
            ...sanityBlockBuilderTextFields
          }
          referralProgramContent {
            ...sanityBlockBuilderTextFields
          }
          referralProgramFallbackMessage {
            ...sanityBlockBuilderTextFields
          }
          referralProgramLink {
            ...sanityLinkFields
          }
          ordersPageHeadline
          ordersPageSubHeadline
          subscriptionsPageHeadline
          subscriptionsPageSubHeadline
          videosPageHeadline
          videosPageSubHeadline
          addressesPageHeadline
          addressesPageSubHeadline
          referralProgramPageHeadline
          referralProgramPageSubHeadline
          referralProgramShareSubject
          referralProgramShareMessage
          appointmentsPageHeadline
          appointmentsPageSubHeadline
          settingsPageHeadline
          settingsPageSubHeadline
          manageHubspotFormFallbackMessage {
            ...sanityBlockBuilderTextFields
          }
          socialMedia {
            youtubeUrl
            youtubeBool
            twitterUrl
            twitterBool
            pinterestUrl
            pinterestBool
            instagramUrl
            instagramBool
            facebookUrl
            facebookBool
          }
          prospectSchedulerLegalConsent {
            ...sanityBlockBuilderTextFields
          }
        }
      }
    `
  );

  const defaultSettings: Partial<AppSiteSettings> = {
    contactPage: '/support/contact',
    phone: '1 (800) 578-3647',
    manageSubscriptionMessage: { text: null },
    manageSubscriptionSupportMessage: { text: null },
    manageSubscriptionCancelMessage: { text: null },

    referralProgramContent: { text: [] },
    referralProgramFallbackMessage: { text: [] },
    referralProgramLink: {},
    referralProgramShareSubject: 'Invisible Fence Referral Program',
    referralProgramShareMessage: '',

    ordersPageHeadline: 'Your Orders',
    ordersPageSubHeadline: 'View your order history and manage your account.',
    subscriptionsPageHeadline: 'Subscriptions',
    subscriptionsPageSubHeadline:
      'Manage your subscriptions, update payment methods, and view subscription details here.',
    videosPageHeadline: 'Training Videos',
    videosPageSubHeadline:
      'Exclusive training videos to help you train your pet with Invisible Fence technology.',
    addressesPageHeadline: 'Your Addresses',
    addressesPageSubHeadline: 'Manage your delivery addresses here.',
    referralProgramPageHeadline: 'Referral Program',
    referralProgramPageSubHeadline: '',
    appointmentsPageHeadline: 'Appointments',
    appointmentsPageSubHeadline: '',
    settingsPageHeadline: 'Your Account',
    settingsPageSubHeadline: 'Manage your account details and password here.',

    manageHubspotFormFallbackMessage: { text: null },
    prospectSchedulerLegalConsent: { text: null },
  };

  const settings: AppSiteSettings = {
    ...defaultSettings,
    ...sanitySiteSettings,
    contactPage: sanitySiteSettings?.contactPage?.slug?.current?.startsWith('/')
      ? sanitySiteSettings.contactPage.slug.current
      : `/${
          sanitySiteSettings?.contactPage?.slug?.current ||
          defaultSettings.contactPage
        }`,
    manageSubscriptionMessage: sanitySiteSettings?.manageSubscriptionMessage
      ?.text
      ? sanitySiteSettings.manageSubscriptionMessage
      : defaultSettings.manageSubscriptionMessage,
    manageSubscriptionSupportMessage: sanitySiteSettings
      ?.manageSubscriptionSupportMessage?.text
      ? sanitySiteSettings.manageSubscriptionSupportMessage
      : defaultSettings.manageSubscriptionSupportMessage,
    manageSubscriptionCancelMessage: sanitySiteSettings
      ?.manageSubscriptionCancelMessage?.text
      ? sanitySiteSettings.manageSubscriptionCancelMessage
      : defaultSettings.manageSubscriptionCancelMessage,
    manageHubspotFormFallbackMessage: sanitySiteSettings
      ?.manageHubspotFormFallbackMessage?.text
      ? sanitySiteSettings.manageHubspotFormFallbackMessage
      : defaultSettings.manageHubspotFormFallbackMessage,
  };

  // Ensure all headlines and sub-headlines have default values if missing
  const headlineFields = [
    'ordersPageHeadline',
    'ordersPageSubHeadline',
    'subscriptionsPageHeadline',
    'subscriptionsPageSubHeadline',
    'videosPageHeadline',
    'videosPageSubHeadline',
    'addressesPageHeadline',
    'addressesPageSubHeadline',
    'referralProgramPageHeadline',
    'referralProgramPageSubHeadline',
    'appointmentsPageHeadline',
    'appointmentsPageSubHeadline',
    'settingsPageHeadline',
    'settingsPageSubHeadline',
  ];

  headlineFields.forEach(field => {
    settings[field] = settings[field] || defaultSettings[field];
  });

  return settings;
};

export default useSiteSettings;
